export type AuthTokenResolver = () => string | null | Promise<string | null>;

let authTokenResolver: AuthTokenResolver | null = null;

export function setAuthTokenResolver(resolver: AuthTokenResolver | null): void {
  authTokenResolver = resolver;
}

export async function resolveAuthHeaderValue(): Promise<string | null> {
  if (!authTokenResolver) {
    return null;
  }

  const token = await authTokenResolver();
  if (!token) {
    return null;
  }

  return `Bearer ${token}`;
}

/**
 * Attempts a silent token renewal (OIDC `signinSilent`) and resolves to the fresh
 * bearer access token, or `null` when renewal is unavailable or fails. Registered by
 * the auth layer so the transport can recover from a 401 before giving up (keeps
 * apis/** transport-only).
 */
export type AuthTokenRenewer = () => Promise<string | null>;

let authTokenRenewer: AuthTokenRenewer | null = null;

export function setAuthTokenRenewer(renewer: AuthTokenRenewer | null): void {
  authTokenRenewer = renewer;
}

let renewInFlight: Promise<string | null> | null = null;

/**
 * Triggers a silent token renewal and returns the fresh access token, or `null` when
 * no renewer is registered or renewal fails. Concurrent calls (a burst of 401s) share
 * a single in-flight renewal so the IdP is only asked once. Once session invalidation
 * has fired, renewal is refused outright: the sign-out/redirect is already underway
 * and query-layer retries must not keep hitting the IdP with a dead refresh token.
 */
export async function attemptTokenRenew(): Promise<string | null> {
  if (!authTokenRenewer || invalidationTriggered) {
    return null;
  }

  if (renewInFlight) {
    return renewInFlight;
  }

  renewInFlight = (async () => {
    try {
      return await authTokenRenewer!();
    } finally {
      renewInFlight = null;
    }
  })();

  return renewInFlight;
}

/**
 * Why the API rejected the session: `sessionExpired` for a 401 that renewal could not
 * fix, `accessRevoked` for the disabled-account 403 (the Entra session is valid, but the
 * account has no Tower membership, so signing in again cannot help).
 */
export type AuthInvalidationReason = "sessionExpired" | "accessRevoked";

/**
 * Called when the API rejects a request as unauthorized (401, or a disabled-account
 * 403). The session/auth layer registers a handler here so the transport layer can
 * trigger recovery/sign-out without importing services (keeps apis/** transport-only).
 */
export type AuthInvalidationHandler = (reason: AuthInvalidationReason) => void;

let authInvalidationHandler: AuthInvalidationHandler | null = null;
let invalidationTriggered = false;

export function setAuthInvalidationHandler(handler: AuthInvalidationHandler | null): void {
  authInvalidationHandler = handler;
  // Registering a handler marks a fresh session (the auth provider mounts once
  // per app load), so reset the one-shot guard below.
  invalidationTriggered = false;
}

/**
 * Invokes the registered handler at most once per session. Returns false when no
 * handler is registered (so the caller can fall back to a plain redirect), and true
 * once a handler exists — including on repeat calls, which are swallowed so a burst
 * of 401s triggers a single sign-out.
 */
export function invokeAuthInvalidation(reason: AuthInvalidationReason = "sessionExpired"): boolean {
  if (!authInvalidationHandler) {
    return false;
  }

  if (invalidationTriggered) {
    return true;
  }

  invalidationTriggered = true;
  authInvalidationHandler(reason);
  return true;
}

